import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Alert,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Collapse,
  Grid
} from '@mui/material';

import { 
  Storage as StorageIcon,
  TableChart as TableIcon,
  Schema as SchemaIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import { 
  getWarehouseSchema,
  getWarehouseTables,
  getWarehouseTableData
} from '../services/api';

import { 
  WarehouseSchema, 
  WarehouseTableInfo, 
  WarehouseTableData 
} from '../types';

import DataTable from '../components/DataTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`warehouse-tabpanel-${index}`}
      aria-labelledby={`warehouse-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DataWarehouse: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [schema, setSchema] = useState<WarehouseSchema | null>(null);
  const [tables, setTables] = useState<WarehouseTableInfo[]>([]);
  const [selectedTableData, setSelectedTableData] = useState<WarehouseTableData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [tableDataLoading, setTableDataLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchWarehouseData();
  }, []);

  const fetchWarehouseData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [schemaData, tablesData] = await Promise.all([
        getWarehouseSchema(),
        getWarehouseTables()
      ]);
      
      setSchema(schemaData);
      setTables(tablesData);
    } catch (err) {
      setError('Failed to fetch warehouse data. Please try again later.');
      console.error('Error fetching warehouse data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewTableData = async (tableName: string) => {
    try {
      setTableDataLoading(true);
      const tableData = await getWarehouseTableData(tableName);
      setSelectedTableData(tableData);
      setTabValue(2); // Switch to data view tab
    } catch (err) {
      console.error('Error fetching table data:', err);
      setError(`Failed to fetch data for table ${tableName}`);
    } finally {
      setTableDataLoading(false);
    }
  };

  const toggleTableExpansion = (tableName: string) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName);
    } else {
      newExpanded.add(tableName);
    }
    setExpandedTables(newExpanded);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Data Warehouse
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchWarehouseData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Warehouse Overview */}
      {schema && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{schema.total_tables}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Tables
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StorageIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{schema.total_records.toLocaleString()}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Records
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SchemaIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{schema.relationships.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Relationships
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {new Date(schema.last_updated).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Schema" icon={<SchemaIcon />} />
            <Tab label="Tables" icon={<TableIcon />} />
            <Tab label="Data View" icon={<ViewIcon />} />
          </Tabs>
        </Box>

        {/* Schema Tab */}
        <TabPanel value={tabValue} index={0}>
          {schema ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Database Schema
              </Typography>
              
              {Object.entries(schema.tables).map(([tableName, table]) => (
                <Paper key={tableName} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">{tableName}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label={`${table.row_count} rows`} size="small" />
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewTableData(tableName)}
                      >
                        View Data
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => toggleTableExpansion(tableName)}
                      >
                        {expandedTables.has(tableName) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {table.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Typography variant="body2">
                      <strong>Primary Key:</strong> {table.primary_key}
                    </Typography>
                    {table.foreign_keys.length > 0 && (
                      <Typography variant="body2">
                        <strong>Foreign Keys:</strong> {table.foreign_keys.join(', ')}
                      </Typography>
                    )}
                  </Box>

                  <Collapse in={expandedTables.has(tableName)}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Columns:
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Column Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {table.columns.map((column) => (
                            <TableRow key={column.name}>
                              <TableCell>
                                <strong>{column.name}</strong>
                                {column.name === table.primary_key && (
                                  <Chip label="PK" size="small" color="primary" sx={{ ml: 1 }} />
                                )}
                                {table.foreign_keys.includes(column.name) && (
                                  <Chip label="FK" size="small" color="secondary" sx={{ ml: 1 }} />
                                )}
                              </TableCell>
                              <TableCell>{column.type}</TableCell>
                              <TableCell>{column.description}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Paper>
              ))}

              {schema.relationships.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Table Relationships
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>From Table</TableCell>
                          <TableCell>To Table</TableCell>
                          <TableCell>Relationship</TableCell>
                          <TableCell>Foreign Key</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schema.relationships.map((rel, index) => (
                          <TableRow key={index}>
                            <TableCell>{rel.from_table}</TableCell>
                            <TableCell>{rel.to_table}</TableCell>
                            <TableCell>{rel.relationship}</TableCell>
                            <TableCell>{rel.foreign_key}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>No schema data available</Typography>
          )}
        </TabPanel>

        {/* Tables Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Warehouse Tables
          </Typography>
          
          {tables.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Table Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Row Count</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tables.map((table) => (
                    <TableRow key={table.name}>
                      <TableCell>
                        <Typography variant="subtitle2">{table.name}</Typography>
                      </TableCell>
                      <TableCell>{table.description}</TableCell>
                      <TableCell>{table.row_count.toLocaleString()}</TableCell>
                      <TableCell>
                        {new Date(table.last_updated).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleViewTableData(table.name)}
                          disabled={tableDataLoading}
                        >
                          View Data
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No tables available</Typography>
          )}
        </TabPanel>

        {/* Data View Tab */}
        <TabPanel value={tabValue} index={2}>
          {tableDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedTableData ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedTableData.table_name} Data
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Showing {selectedTableData.data.length} of {selectedTableData.total_rows} records
              </Typography>
              
              <DataTable 
                data={selectedTableData.data}
                title={selectedTableData.table_name}
                columns={selectedTableData.columns.map(col => ({
                  id: col,
                  label: col,
                  format: (value: any) => value?.toString() || ''
                }))}
                metadata={{
                  unique_records: selectedTableData.unique_records,
                  duplicate_count: selectedTableData.duplicate_count,
                  total_raw_records: selectedTableData.total_raw_records,
                  deduplication_info: selectedTableData.deduplication_info
                }}
              />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="body1" color="text.secondary">
                Select a table from the Tables tab to view its data
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Card>
    </div>
  );
};

export default DataWarehouse;
